import {
  Alert,
  Badge,
  Button,
  Code,
  Flex,
  Group,
  Loader,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
  useTree,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowLeft, IconPlayerPlay } from '@tabler/icons-react';
import { useRef, useState, type RefObject } from 'react';
import { COLUMN_HEIGHT, CopyIconButton, ExplorerPanel, MethodBadge } from './ExplorerParts';
import { setJsonValue } from './json-path';
import { describeJsonError, type JsonSyntaxError } from './json-syntax-error';
import JsonTree from './JsonTree';
import RawJsonEditor from './RawJsonEditor';
import { canRunCalculator, runCalculator, type CalculatorRunResult } from './run-calculator';
import type { SpecOperation } from './spec-tree';

type ExecuteRequestPanelProps = {
  calculator: string;
  version: string | undefined;
  operation: SpecOperation;
  initialBody: unknown;
  onBack: () => void;
};

const STATUS_BADGES: Record<CalculatorRunResult['status'], { color: string; label: string }> = {
  OK: { color: 'teal', label: 'Success' },
  INVALID_INPUT: { color: 'orange', label: 'Invalid input' },
  ERROR: { color: 'red', label: 'Error' },
};

type EditorMode = 'form' | 'raw';

const formatJson = (value: unknown) => JSON.stringify(value, null, 2);

// Emission entries with nothing in them are noise when reading a result, so they start collapsed
const hasZeroAmount = (value: object) => 'amount' in value && value.amount === 0;

function ResultView({ result, scrollRef }: { result: CalculatorRunResult; scrollRef: RefObject<HTMLElement> }) {
  const resultTree = useTree();

  if (result.status === 'OK')
    return (
      <JsonTree
        value={result.emissions}
        tree={resultTree}
        scrollRef={scrollRef}
        expandDepth={2}
        startCollapsed={hasZeroAmount}
      />
    );

  if (result.status === 'INVALID_INPUT') {
    return (
      <Stack gap="sm" p="xs">
        <Text size="sm">{result.message}</Text>
        <Table striped verticalSpacing={4} fz="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Path</Table.Th>
              <Table.Th>Problem</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.issues.map((issue, position) => (
              <Table.Tr key={`${issue.path}-${position}`}>
                <Table.Td>
                  <Code>{issue.path || '(root)'}</Code>
                </Table.Td>
                <Table.Td>{issue.message}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    );
  }

  return (
    <Alert color="red" title="The calculation failed" m="xs">
      {result.message}
    </Alert>
  );
}

function ExecuteRequestPanel({ calculator, version, operation, initialBody, onBack }: ExecuteRequestPanelProps) {
  const [body, setBody] = useState(initialBody);
  const [mode, setMode] = useState<EditorMode>('form');
  const [rawText, setRawText] = useState(() => formatJson(initialBody));
  const [rawError, setRawError] = useState<JsonSyntaxError | null>(null);
  const [result, setResult] = useState<CalculatorRunResult | null>(null);
  const [running, setRunning] = useState(false);
  const requestTree = useTree();
  const requestViewport = useRef<HTMLDivElement>(null);
  const resultViewport = useRef<HTMLDivElement>(null);
  const runnable = canRunCalculator(version);

  const changeMode = (next: EditorMode) => {
    if (next === 'raw') {
      setRawText(formatJson(body));
      setRawError(null);
    }
    setMode(next);
  };

  const editRaw = (text: string) => {
    setRawText(text);
    try {
      setBody(JSON.parse(text));
      setRawError(null);
    } catch (error) {
      setRawError(describeJsonError(text, error));
    }
  };

  const reset = () => {
    setBody(initialBody);
    setRawText(formatJson(initialBody));
    setRawError(null);
  };

  const execute = async () => {
    setRunning(true);
    // Let the loading state paint before the calculation blocks the main thread
    await new Promise((resolve) => setTimeout(resolve, 0));
    setResult(await runCalculator(version, calculator, body));
    setRunning(false);
  };

  return (
    <Stack gap="md" h={COLUMN_HEIGHT} className="module-explorer-layer">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back to modules
          </Button>
          <MethodBadge method={operation.method} />
          <Code>{operation.path}</Code>
        </Group>
        <Button
          leftSection={<IconPlayerPlay size={16} />}
          onClick={execute}
          loading={running}
          disabled={!runnable || rawError !== null}
          title={
            !runnable
              ? `Requests cannot be executed for version ${version} yet`
              : rawError
                ? 'Fix the invalid JSON before executing'
                : undefined
          }
        >
          Execute request
        </Button>
      </Group>

      <Flex gap="md" flex={1} mih={0}>
        <ExplorerPanel
          title="Request"
          flex={1}
          miw={0}
          controls={
            <SegmentedControl
              size="xs"
              data={[
                { value: 'form', label: 'Form', disabled: rawError !== null },
                { value: 'raw', label: 'Raw' },
              ]}
              value={mode}
              onChange={(value) => changeMode(value as EditorMode)}
            />
          }
          actions={
            <>
              <Button
                variant="subtle"
                size="compact-xs"
                onClick={reset}
                disabled={body === initialBody && rawError === null}
              >
                Reset
              </Button>
              {mode === 'form' && (
                <>
                  <Button variant="subtle" size="compact-xs" onClick={() => requestTree.expandAllNodes()}>
                    Expand all
                  </Button>
                  <Button variant="subtle" size="compact-xs" onClick={() => requestTree.collapseAllNodes()}>
                    Collapse all
                  </Button>
                </>
              )}
            </>
          }
        >
          <ScrollArea flex={1} p="xs" viewportRef={requestViewport}>
            {mode === 'form' ? (
              <JsonTree
                value={body}
                tree={requestTree}
                scrollRef={requestViewport}
                onChange={(path, value) => setBody((current: unknown) => setJsonValue(current, path, value))}
              />
            ) : (
              <RawJsonEditor
                value={rawText}
                onChange={editRaw}
                errorLine={rawError?.line}
                invalid={rawError !== null}
              />
            )}
          </ScrollArea>
          {mode === 'raw' && rawError && (
            <Alert
              color="red"
              variant="light"
              icon={<IconAlertTriangle size={16} />}
              title="Invalid JSON"
              m="xs"
              mt={0}
            >
              <Text size="sm">{rawError.message}</Text>
              <Text size="xs" c="dimmed" mt={4}>
                Fix the JSON to switch back to the form or execute the request.
              </Text>
            </Alert>
          )}
        </ExplorerPanel>

        <ExplorerPanel
          title="Result"
          flex={1}
          miw={0}
          actions={
            result &&
            !running && (
              <>
                <Badge color={STATUS_BADGES[result.status].color} variant="light" radius="sm" tt="none">
                  {STATUS_BADGES[result.status].label}
                </Badge>
                {/* A successful run copies just the emissions shown, anything else copies the whole error */}
                <CopyIconButton
                  value={formatJson(result.status === 'OK' ? result.emissions : result)}
                  label="Copy JSON"
                />
              </>
            )
          }
        >
          <ScrollArea flex={1} p="xs" viewportRef={resultViewport}>
            {running ? (
              <Group justify="center" p="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                  Running the {calculator} calculator...
                </Text>
              </Group>
            ) : result ? (
              <ResultView result={result} scrollRef={resultViewport} />
            ) : (
              <Text c="dimmed" size="sm" p="xs">
                {runnable
                  ? `Execute the request to run it through the ${calculator} calculator and see the result here.`
                  : `Requests cannot be executed for version ${version} yet.`}
              </Text>
            )}
          </ScrollArea>
        </ExplorerPanel>
      </Flex>
    </Stack>
  );
}

export default ExecuteRequestPanel;
